/**
 * Campaign generator — produces schema-accurate `CampaignMetrics[]` matching
 * the `campaign_metrics` N8N action response (types/n8nResponses.ts:40).
 *
 * Each campaign is seeded by its index so volumes overlap: campaign #2 in
 * "some" is identical to campaign #2 in "huge". This keeps the demo stable
 * as the user toggles volumes.
 */

import type { CampaignMetrics } from '../../../types/n8nResponses';
import type { MockVolume } from '../mockDataVolume';
import { mulberry32, intBetween, pick, weightedPick } from '../rng';
import {
  fill,
  CAMPAIGN_THEMES, CAMPAIGN_LOCATIONS, CAMPAIGN_OBJECTIVES,
  CAMPAIGN_CHANNELS, CAMPAIGN_STATUS, INDUSTRIES,
  VALUE_HOOKS, REVENUE_RANGES,
} from '../dictionaries';

/** Volume → campaign count. `none` is short-circuited by the caller. */
export const CAMPAIGN_COUNT_BY_VOLUME: Record<Exclude<MockVolume, 'none'>, number> = {
  some: 3,
  huge: 12,
};

// Realistic funnel distribution for newly-generated leads BEFORE outreach.
// Heavier at the top (leads/enriching) so the Kanban Lead Pool stays full.
const LEAD_COUNT_WEIGHTS: ReadonlyArray<readonly [number, number]> = [
  [8, 2], [10, 3], [12, 3], [15, 4], [20, 4], [28, 3], [40, 2], [60, 2], [80, 1],
];

const MOCK_SELLERS = [
  { id: 'seller-ingrid-chen', name: 'Ingrid Chen' },
  { id: 'seller-nick-garcia', name: 'Nick Garcia' },
  { id: 'seller-laura-martinez', name: 'Laura Martinez' },
];

/** Rough target lead count for a campaign at a given volume tier. */
export const targetLeadCountFor = (volume: Exclude<MockVolume, 'none'>): number => {
  const scale = volume === 'huge' ? 5 : 1;
  const rng = mulberry32(99); // fixed so the "per campaign" range is stable
  return weightedPick(LEAD_COUNT_WEIGHTS, rng) * scale;
};

const toIsoDaysAgo = (days: number): string => {
  // Fixed "today" mirrors the dummy dataset (2026-07-01) so relative labels stay stable.
  const base = new Date('2026-07-01T12:00:00Z').getTime();
  return new Date(base - days * 86_400_000).toISOString();
};

const toIsoDaysAhead = (days: number): string => {
  const base = new Date('2026-07-01T12:00:00Z').getTime();
  return new Date(base + days * 86_400_000).toISOString();
};

/** Stable, short, lowercase id from an index (looks like real N8N campaign ids). */
export const campaignIdFor = (index: number): string => {
  const rng = mulberry32(1000 + index);
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  for (let i = 0; i < 8; i++) id += chars[Math.floor(rng() * chars.length)];
  return id;
};

/**
 * Generate `count` campaigns. `userEmail` populates client_email/client_name so
 * the dataset looks owned by the signed-in user (mirrors what n8n returns).
 */
export const generateMockCampaigns = (
  count: number,
  userEmail: string,
): CampaignMetrics[] => {
  const campaigns: CampaignMetrics[] = [];

  for (let i = 0; i < count; i++) {
    const rng = mulberry32(5000 + i);
    const theme = pick(CAMPAIGN_THEMES, rng);
    const createdDaysAgo = intBetween(20, 120, rng);
    const durationDays = intBetween(60, 180, rng);
    const statusRoll = rng();
    const seller = MOCK_SELLERS[i % MOCK_SELLERS.length];

    // Weighted: most campaigns active, a few paused/archived/completed.
    const status = weightedPick<CampaignMetrics['status']>([
      ['active', 5],
      ['paused', 2],
      ['archived', 1],
      ['completed', statusRoll > 0.5 ? 1 : 2],
    ], rng);

    const channels = Array.from(new Set([
      'email',
      ...Array.from({ length: intBetween(1, 2, rng) }, () => pick(CAMPAIGN_CHANNELS, rng)),
    ]));

    campaigns.push({
      campaign_id: campaignIdFor(i),
      campaign_name: `${theme}`,
      status,
      created: toIsoDaysAgo(createdDaysAgo),
      client_email: userEmail,
      client_name: userEmail.split('@')[0]?.replace(/[._]/g, ' ') || 'Mock Client',
      account_manager_id: null,
      seller_lvl1: seller.id,
      seller_name: seller.name,

      // Campaign details
      summary: `${theme} — targeting ${pick(INDUSTRIES, rng)} buyers across ${pick(CAMPAIGN_LOCATIONS, rng)}.`,
      product_website: `https://${theme.toLowerCase().replace(/\s+/g, '')}.example.com`,
      target_audience_niche: pick(INDUSTRIES, rng),
      primary_campaign_objective: pick(CAMPAIGN_OBJECTIVES, rng),
      search_location: pick(CAMPAIGN_LOCATIONS, rng),
      outreach_channel: channels.map((value) => ({ value, id: value })),
      desires: `Wants ${pick(VALUE_HOOKS, rng).toLowerCase()}`,
      core_value_proposition: pick(VALUE_HOOKS, rng),
      success_metrics: 'Booked demos, reply rate > 8%, pipeline coverage 3x',
      buyer_personas: `${pick(['VP Sales', 'Head of Growth', 'CRO', 'Head of RevOps'], rng)} at ${pick(INDUSTRIES, rng)} (50-500 employees)`,
      best_outreach_timing: 'Tue–Thu, 9–11am local time',
      target_audience_pain_points: 'Manual research, low personalization, no attribution',
      sales_frameworks: { value: 'Challenger', id: 'challenger' },
      competitor_analysis: 'Apollo, Lusha, ZoomInfo — we win on personalization depth',
      industry_keyworks: pick(INDUSTRIES, rng),
      other_notes: null,
      source: 'lead-intelligence-app-mock',
      updated_at: toIsoDaysAgo(intBetween(1, 7, rng)),
      updatedAt: toIsoDaysAgo(intBetween(1, 7, rng)),
      block_outreach: false,
      available_outreach_assets: ['Case study', 'One-pager', 'Demo video'],
      resources_assigned: { sdr: ['ai-agent-1'], sequence: ['default-3-touch'] },
      usp: pick(VALUE_HOOKS, rng),
      unique_sales_point: pick(VALUE_HOOKS, rng),
      unique_selling_point: pick(VALUE_HOOKS, rng),
      guarantee: 'Reply within 24h or the next month is free',
      offer_pricing: 'From €490/mo per team of 10',
      campaign_context_file: null,

      // Dates & limits
      start: toIsoDaysAgo(createdDaysAgo),
      done: status === 'completed' ? toIsoDaysAgo(intBetween(1, 19, rng)) : toIsoDaysAhead(durationDays - createdDaysAgo),
      start_date: toIsoDaysAgo(createdDaysAgo),
      end_date: status === 'completed' ? toIsoDaysAgo(intBetween(1, 19, rng)) : toIsoDaysAhead(durationDays - createdDaysAgo),
      weekly_limit: intBetween(20, 80, rng),

      // Metrics — zeroed here; the lead generator recomputes these from the
      // actual generated leads so the campaign summary cards stay consistent.
      total_leads: 0,
      leads_by_status: {
        new_lead: 0,
        enriching: 0,
        enriched: 0,
        outreaching: 0,
        outreached: 0,
        engaged: 0,
        proposal: 0,
        converted: 0,
        discarded: 0,
      },
    } satisfies CampaignMetrics);
  }

  return campaigns;
};
